import React from "react";
import { useTranslation } from "react-i18next";
import { DeviceCommandProvider } from "src/providers/DeviceCommandProvider";
import { useAppSettingsService } from "src/services/appSettingsService/appSettingsService";
import { useDispatch, useSelector } from "src/store";

import { License } from "@dashboard/devices/types";
import { getSummaryIndicatorData } from "@dashboard/devices/utils/license";
import CommandsTab from "@dashboard/shared/components/SidePanel/LicensePanel/CommandsTab/CommandsTab";
import { useCurrentSubscription } from "@dashboard/workspaces/api/helpers";
import { closeDetails, selectDetailsLicense, selectPaneTab, setPaneTab } from "@devices/store";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import { Box, Typography, useTheme } from "@mui/material";
import { ScreenViewButton } from "@screenView/ui/ScreenViewButton/ScreenViewButton";
import { getSettings } from "@services/configService";
import { SecondaryButton } from "@shared/CustomButton";
import { Tooltip } from "@shared/Tooltip";

import { MenuLicense } from "../shared/MenuLicense";
import { PaneElement } from "../shared/PaneElement";
import { PaneTabPanel } from "../shared/PaneTabPanel";
import { useStyle } from "../shared/style";
import { Tab } from "../shared/Tab";

import AppTab from "./AppTab/AppTab";
import BwcTab from "./BwcTab/BwcTab";
import EventTab from "./EventTab/EventTab";
import FileTab from "./FileTab/FileTab";
import HardwareTab from "./HardwareTab/HardwareTab";
import { LogButton } from "./LogButton/LogButton";
import NetworkTab from "./NetworkTab/NetworkTab";
import DeviceOverviewTab from "./OverviewTab/DeviceOverviewTab";
import LicenseOverviewTab from "./OverviewTab/LicenseOverviewTab";
import SystemTab from "./SystemTab/SystemTab";
import LicenseTab from "./LicenseTab";
import { VncButton } from "./VncButon";

const generalTabs = ["OVERVIEW", "LICENSE"];
const deviceTabs = ["APPS", "FILES", "COMMANDS", "EVENTS"];
const deviceStateTabs = ["OS", "HARDWARE", "NETWORK", "BWC"];

function getLicenseSubtitle(license: License | null): string | null {
    if (!license) {
        return null;
    }

    if (license.device?.description) {
        return license.device.description;
    }

    if (!license.device) {
        if (license.comment) {
            return license.comment;
        }
        if (license.activationCode) {
            return license.activationCode;
        }
    }

    return null;
}

export const LicenseSidePanel = () => {
    const license = useSelector(selectDetailsLicense);
    const dispatch = useDispatch();
    const { classes } = useStyle();
    const { t } = useTranslation(["sidepanels", "common"]);
    const { features } = getSettings();
    const theme = useTheme();
    const device = license?.device;
    const { effectiveWorkspaceSettings } = useAppSettingsService();
    // One condition per tab, used by both the tab strip and the panel list below. They used to be written out
    // twice, in different orders and with slightly different spellings of the same check, so a one-sided edit
    // would have produced a visible tab with an empty pane.
    const tabAvailable = {
        license: features.devicePanel.licenseTabEnabled,
        files: features.devicePanel.filesTabEnabled && effectiveWorkspaceSettings.isFilesPageEnabled && !!device?.lastState,
        apps: features.devicePanel.appsTabEnabled && !!device,
        bwc: features.devicePanel.bwcTabEnabled && !!device?.lastState,
        os: features.devicePanel.osTabEnabled && !!device?.lastState,
        hardware: features.devicePanel.hardwareTabEnabled && !!device?.lastState,
        network: features.devicePanel.networkTabEnabled && !!device?.lastState,
        events: features.devicePanel.eventsTabEnabled && !!device,
        commands: features.devicePanel.commandsTabEnabled && !!device,
    };

    const tabs = [...generalTabs, ...(license?.device ? deviceTabs : []), ...(license?.device?.lastState ? deviceStateTabs : [])];

    const value = useSelector(selectPaneTab);
    const isLicenseTab = tabs.includes(value);

    const { data: subscription } = useCurrentSubscription({});
    const isValid = !!subscription?.isValid;
    const isExpired = !!subscription?.isExpired;
    const statusIndicator = getSummaryIndicatorData(license, isExpired, theme, isValid);

    const subtitle = getLicenseSubtitle(license);

    const onChange = (_event: React.ChangeEvent<object>, tabTitle: string) => {
        dispatch(setPaneTab(tabTitle));
    };

    React.useEffect(() => {
        if (!isLicenseTab) {
            dispatch(setPaneTab("OVERVIEW"));
        }
    }, [dispatch, isLicenseTab]);

    if (!isLicenseTab || !license) {
        return null;
    }

    return (
        <DeviceCommandProvider>
            <TabContext value={value}>
                <PaneElement.HeaderBox>
                    <Box className={classes.headerBox}>
                        <Box className={classes.licenseSummaryBox}>
                            <Tooltip title={statusIndicator.tooltip}>
                                <Box
                                    className={classes.licenseSummaryIndicator}
                                    style={{ backgroundColor: statusIndicator.color }}
                                />
                            </Tooltip>
                            <Typography variant="h6" className={classes.panelTitle}>
                                {license.device?.name || t("sidepanels:devicePanel.overviewTab.deviceSlot")}
                            </Typography>
                        </Box>
                        {subtitle && (
                            <Typography variant="body1" className={classes.panelSubtitle}>
                                {subtitle}
                            </Typography>
                        )}
                    </Box>

                    <Box className={classes.buttonBox}>
                        <LogButton {...license} />
                        <VncButton {...license} />
                        <ScreenViewButton {...license} />
                        <MenuLicense {...license} />
                        <SecondaryButton onClick={() => dispatch(closeDetails())}>
                            <i className="fa-solid fa-xmark" />
                        </SecondaryButton>
                    </Box>
                </PaneElement.HeaderBox>

                <TabList onChange={onChange} variant="scrollable" scrollButtons>
                    <Tab
                        value="OVERVIEW"
                        label={t("sidepanels:devicePanel.overviewTab.title")}
                        icon={<i className="fa-solid fa-gauge-high" />}
                    />
                    {tabAvailable.license ? (
                        <Tab
                            value="LICENSE"
                            label={t("sidepanels:devicePanel.licenseTab.title")}
                            icon={<i className="fa-solid fa-desktop" />}
                        />
                    ) : null}

                    {tabAvailable.apps ? (
                        <Tab
                            value="APPS"
                            label={t("sidepanels:devicePanel.appsTab.title")}
                            icon={<i className="fa-solid fa-table-cells" />}
                        />
                    ) : null}
                    {tabAvailable.bwc ? (
                        <Tab
                            value="BWC"
                            label={t("sidepanels:devicePanel.bwcTab.title")}
                            icon={<i className="fa-solid fa-video" />}
                        />
                    ) : null}
                    {tabAvailable.files ? (
                        <Tab
                            value="FILES"
                            label={t("sidepanels:devicePanel.filesTab.title")}
                            icon={<i className="fa-solid fa-file" />}
                        />
                    ) : null}

                    {tabAvailable.os ? (
                        <Tab
                            value="OS"
                            label={t("sidepanels:devicePanel.osTab.title")}
                            icon={<i className="fa-solid fa-desktop" />}
                        />
                    ) : null}
                    {tabAvailable.hardware ? (
                        <Tab
                            value="HARDWARE"
                            label={t("sidepanels:devicePanel.hardwareTab.title")}
                            icon={<i className="fa-solid fa-microchip" />}
                        />
                    ) : null}
                    {tabAvailable.network ? (
                        <Tab
                            value="NETWORK"
                            label={t("sidepanels:devicePanel.networkTab.title")}
                            icon={<i className="fa-solid fa-network-wired" />}
                        />
                    ) : null}
                    {tabAvailable.events ? (
                        <Tab value="EVENTS" label={t("common:label.events")} icon={<i className="fa-solid fa-bell" />} />
                    ) : null}
                    {tabAvailable.commands ? (
                        <Tab
                            value="COMMANDS"
                            label={t("sidepanels:devicePanel.commandsTab.title")}
                            icon={<i className="fa-solid fa-terminal" />}
                        />
                    ) : null}
                </TabList>

                <Box className={classes.contentBox}>
                    <PaneTabPanel value="OVERVIEW">
                        {license.device ? <DeviceOverviewTab {...license} /> : <LicenseOverviewTab {...license} />}
                    </PaneTabPanel>
                    {tabAvailable.license ? (
                        <PaneTabPanel value="LICENSE">
                            <LicenseTab {...license} />
                        </PaneTabPanel>
                    ) : null}
                    {tabAvailable.os && device?.lastState ? (
                        <PaneTabPanel value="OS">
                            <SystemTab deviceState={device.lastState} license={license} />
                        </PaneTabPanel>
                    ) : null}
                    {tabAvailable.hardware && device?.lastState ? (
                        <PaneTabPanel value="HARDWARE">
                            <HardwareTab deviceState={device.lastState} />
                        </PaneTabPanel>
                    ) : null}
                    {tabAvailable.network && device?.lastState ? (
                        <PaneTabPanel value="NETWORK">
                            <NetworkTab deviceId={device.id} deviceState={device.lastState} />
                        </PaneTabPanel>
                    ) : null}
                    {tabAvailable.apps && device ? (
                        <PaneTabPanel value="APPS" fill>
                            <AppTab device={device} groupId={license.groupId} />
                        </PaneTabPanel>
                    ) : null}
                    {tabAvailable.bwc && device?.lastState ? (
                        <PaneTabPanel value="BWC">
                            <BwcTab device={device} groupId={license.groupId} groupName={license.groupName} />
                        </PaneTabPanel>
                    ) : null}
                    {tabAvailable.files && device ? (
                        <PaneTabPanel value="FILES" fill>
                            <FileTab device={device} groupId={license.groupId} />
                        </PaneTabPanel>
                    ) : null}
                    {tabAvailable.commands && device ? (
                        <PaneTabPanel value="COMMANDS" fill>
                            <CommandsTab device={device} />
                        </PaneTabPanel>
                    ) : null}
                    {tabAvailable.events && device ? (
                        <PaneTabPanel value="EVENTS" fill>
                            <EventTab device={device} />
                        </PaneTabPanel>
                    ) : null}
                </Box>
            </TabContext>
        </DeviceCommandProvider>
    );
};
